import { useEffect, useMemo, useState } from 'react'
import { coursesAPI } from '../api/courses'
import { exercisesAPI } from '../api/exercises'
import {
  buildCourseExerciseOrder,
  findPendingPrerequisites,
  type ModuleLike,
  type PendingPrerequisite,
  type ProgressLike
} from '../lib/prerequisites'

interface UseCoursePrerequisitesArgs {
  courseId: string | undefined
  moduleId: string | undefined
  /** Live exercises of the module being viewed — kept in sync with resets. */
  moduleExercises: ProgressLike[]
}

interface UseCoursePrerequisitesResult {
  getPendingPrerequisites: (exerciseId: number) => PendingPrerequisite[]
}

/**
 * Loads the whole course (modules + their exercises) so we can tell whether an
 * exercise the learner is about to open has unfinished exercises before it.
 *
 * If the course can't be loaded we fall back to the current module on its own,
 * which still catches the common "skipped exercise 2" case.
 */
export const useCoursePrerequisites = ({
  courseId,
  moduleId,
  moduleExercises
}: UseCoursePrerequisitesArgs): UseCoursePrerequisitesResult => {
  const currentModuleId = moduleId ? Number(moduleId) : undefined
  const [modules, setModules] = useState<ModuleLike[]>([])
  const [otherModuleExercises, setOtherModuleExercises] = useState<Record<number, ProgressLike[]>>({})

  useEffect(() => {
    let cancelled = false

    const fetchCourse = async (): Promise<void> => {
      if (!courseId || currentModuleId === undefined) return
      try {
        const courseModules: ModuleLike[] = await coursesAPI.getCourseModules(courseId)
        if (cancelled || !Array.isArray(courseModules)) return
        setModules(courseModules)

        const results = await Promise.all(
          courseModules
            .filter((m) => m.id !== currentModuleId)
            .map((m) =>
              exercisesAPI
                .getModuleExercises(String(m.id))
                .then((exercises: ProgressLike[] | null) => ({
                  id: m.id,
                  exercises: Array.isArray(exercises) ? exercises : []
                }))
                .catch(() => ({ id: m.id, exercises: [] as ProgressLike[] }))
            )
        )
        if (cancelled) return

        const map: Record<number, ProgressLike[]> = {}
        results.forEach(({ id, exercises }) => {
          map[id] = exercises
        })
        setOtherModuleExercises(map)
      } catch {
        // Course-wide lookup failed — the module-only fallback below still applies.
      }
    }

    setModules([])
    setOtherModuleExercises({})
    fetchCourse()

    return () => {
      cancelled = true
    }
  }, [courseId, currentModuleId])

  const orderedExercises = useMemo(() => {
    if (currentModuleId === undefined) return []

    const knownModules = modules.some((m) => m.id === currentModuleId)
      ? modules
      : [...modules, { id: currentModuleId, title: '', order_index: null }]

    return buildCourseExerciseOrder(knownModules, {
      ...otherModuleExercises,
      [currentModuleId]: moduleExercises
    })
  }, [modules, otherModuleExercises, moduleExercises, currentModuleId])

  const getPendingPrerequisites = (exerciseId: number): PendingPrerequisite[] =>
    findPendingPrerequisites(orderedExercises, exerciseId, currentModuleId)

  return { getPendingPrerequisites }
}
